import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { toast } from 'sonner';
import {
  Plus,
  Download,
  LayoutGrid,
  List,
  RefreshCcw,
  AlertTriangle,
  TrendingUp,
  CheckCircle,
  FileText,
  CalendarClock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import LogoLoader from '@/components/LogoLoader';
import { formatDisplayDate } from '@/utils/date';

const STATUS_LABELS = {
  draft: 'Draft',
  sent: 'Issued',
  viewed: 'Viewed',
  approved: 'Approved',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void'
};

const OPEN_STATUSES = ['draft', 'sent', 'viewed', 'approved', 'overdue'];
const STATUS_ORDER = ['overdue', 'approved', 'sent', 'viewed', 'draft', 'paid', 'void'];
const STATUS_FILTERS = [
  { value: 'open', label: 'Open invoices' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'draft', label: 'Drafts' },
  { value: 'sent', label: 'Issued' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Voided' },
  { value: 'all', label: 'All invoices' }
];

const formatCurrency = (value) => {
  const amount = Number.isFinite(value) ? Number(value) : 0;
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const calculateDaysUntil = (dateString) => {
  if (!dateString) return null;
  const due = new Date(dateString);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  due.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return Math.round((due - today) / (1000 * 60 * 60 * 24));
};

const describeDueDate = (dateString) => {
  const diff = calculateDaysUntil(dateString);
  if (diff === null) return 'No due date';
  if (diff === 0) return 'Due today';
  if (diff > 0) return `Due in ${diff} day${diff === 1 ? '' : 's'}`;
  return `${Math.abs(diff)} day${Math.abs(diff) === 1 ? '' : 's'} overdue`;
};

const transformInvoiceResponse = (invoice, amountPaid = 0) => {
  const accumulator = (invoice?.line_items || []).reduce(
    (acc, item, index) => {
      const quantity = Number(item?.quantity ?? 0);
      const rate = Number(item?.unit_price ?? 0);
      const taxRate = Number(item?.tax_rate ?? 0);
      const lineSubtotal = quantity * rate;
      const lineTax = lineSubtotal * taxRate;
      const lineTotal = lineSubtotal + lineTax;

      acc.lines.push({
        id: `${invoice.invoice_id}-line-${index}`,
        line_type: 'Service',
        description: item?.description ?? 'Line item',
        period_start: item?.period_start ?? null,
        period_end: item?.period_end ?? null,
        consultant_id: item?.consultant_id ?? null,
        quantity_hours: Number.isFinite(quantity) ? quantity : 0,
        rate: Number.isFinite(rate) ? rate : 0,
        amount: Number.isFinite(lineTotal) ? Number(lineTotal.toFixed(2)) : 0,
        po_reference: item?.po_reference ?? null
      });
      acc.subtotal += Number.isFinite(lineSubtotal) ? lineSubtotal : 0;
      acc.tax += Number.isFinite(lineTax) ? lineTax : 0;
      return acc;
    },
    { lines: [], subtotal: 0, tax: 0 }
  );

  if (accumulator.lines.length === 0) {
    accumulator.lines.push({
      id: `${invoice.invoice_id}-line-0`,
      line_type: 'Service',
      description: invoice?.notes || 'Invoice total',
      period_start: null,
      period_end: null,
      consultant_id: null,
      quantity_hours: 1,
      rate: Number(invoice?.totals?.subtotal ?? invoice?.total ?? 0),
      amount: Number(invoice?.total ?? 0),
      po_reference: null
    });
    accumulator.subtotal = Number(invoice?.totals?.subtotal ?? invoice?.total ?? 0);
    accumulator.tax = 0;
  }

  const subtotal = Number(accumulator.subtotal.toFixed(2));
  const taxAmount = Number(accumulator.tax.toFixed(2));
  const total = Number((subtotal + taxAmount).toFixed(2));
  const amountPaidRounded = Number(amountPaid.toFixed(2));
  const balance = Number(Math.max(total - amountPaidRounded, 0).toFixed(2));

  return {
    id: invoice.invoice_id,
    invoice_no: invoice.invoice_id,
    client_id: invoice.client_id,
    project_id: invoice.project_id ?? '',
    po_id: invoice.po_id ?? null,
    invoice_date: invoice.issue_date,
    due_date: invoice.due_date,
    status: STATUS_LABELS[invoice.status] || invoice.status,
    domainStatus: invoice.status,
    subtotal,
    tax_rate: Number(invoice?.line_items?.[0]?.tax_rate ?? 0),
    tax_amount: taxAmount,
    total,
    amount_paid: amountPaidRounded,
    balance,
    lines: accumulator.lines,
    line_items: invoice.line_items || [],
    notes: invoice.notes,
    currency: invoice.currency
  };
};

const Invoices = () => {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pos, setPos] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [poSummarySelections, setPoSummarySelections] = useState({});
  const [statusFilter, setStatusFilter] = useState('open');
  const [clientFilter, setClientFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [formData, setFormData] = useState({
    client_id: '',
    project_id: '',
    po_id: '',
    period_start: '',
    period_end: '',
    tax_rate: '0'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [invoicesData, clientsData, projectsData, posData] = await Promise.all([
        api.get('/accounting/invoices?scope=all', token),
        api.get('/clients', token),
        api.get('/projects', token),
        api.get('/purchase-orders', token)
      ]);
      const invoicesWithPayments = await Promise.all(
        invoicesData.map(async (invoice) => {
          try {
            const payments = await api.get(`/accounting/invoices/${invoice.invoice_id}/payments`, token);
            const amountPaid = payments.reduce((sum, payment) => sum + Number(payment.amount ?? 0), 0);
            return transformInvoiceResponse(invoice, amountPaid);
          } catch (error) {
            return transformInvoiceResponse(invoice, 0);
          }
        })
      );
      setInvoices(invoicesWithPayments);
      setClients(clientsData);
      setProjects(projectsData);
      setPos(posData);
      setPoSummarySelections({});
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        tax_rate: parseFloat(formData.tax_rate)
      };
      const invoice = await api.post('/accounting/invoices/generate-from-time-entries', payload, token);
      toast.success(`Invoice ${invoice.invoice_id || invoice.invoice_no || 'draft'} generated successfully`);
      setShowDialog(false);
      loadData();
      setFormData({ client_id: '', project_id: '', po_id: '', period_start: '', period_end: '', tax_rate: '0' });
    } catch (error) {
      toast.error('Failed to generate invoice');
    }
  };

  const handleStatusUpdate = async (invoiceId, newStatus) => {
    try {
      await api.patch(`/accounting/invoices/${invoiceId}/status`, { status: newStatus }, token);
      toast.success('Invoice status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const viewInvoiceDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailsDialog(true);
  };

  const setIncludePoSummary = (invoiceId, value) => {
    setPoSummarySelections((prev) => ({
      ...prev,
      [invoiceId]: value,
    }));
  };

  const sanitizeFilenamePart = (value) => {
    if (!value) return '';
    return value
      .toString()
      .trim()
      .replace(/[^A-Za-z0-9._-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^[_\-.]+|[_\-.]+$/g, '');
  };

  const buildInvoiceFilename = (invoice) => {
    if (!invoice) {
      return '';
    }

    const sanitizeWithFallback = (value, fallback) => {
      const cleaned = sanitizeFilenamePart(value);
      return cleaned || fallback;
    };

    const invoicePart = sanitizeWithFallback(invoice.invoice_no || invoice.invoice_id || invoice.id, 'invoice');
    const clientPart = sanitizeWithFallback(getClientName(invoice.client_id), 'client');

    let periodPart = '';
    if (Array.isArray(invoice.lines) && invoice.lines.length > 0) {
      const starts = invoice.lines
        .map((line) => line?.period_start)
        .filter(Boolean)
        .sort();
      const ends = invoice.lines
        .map((line) => line?.period_end)
        .filter(Boolean)
        .sort();
      const first = starts[0];
      const last = ends[ends.length - 1];
      if (first && last) {
        periodPart = `${first}_to_${last}`;
      } else if (first || last) {
        periodPart = first || last;
      }
    }

    if (!periodPart) {
      periodPart = invoice.period_label || invoice.invoice_date;
    }

    periodPart = sanitizeWithFallback(
      periodPart || invoice.period_label || invoice.invoice_date,
      'period'
    );

    return `${invoicePart}_${clientPart}_${periodPart}.pdf`;
  };

  const downloadInvoicePDF = async (invoice) => {
    try {
      const includePoSummary = !!poSummarySelections[invoice.id];
      const summaryQuery = includePoSummary ? '?include_po_summary=true' : '';

      const invoiceKey = invoice.invoice_id || invoice.id;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounting/invoices/${invoiceKey}/pdf${summaryQuery}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const contentDisposition = response.headers.get('content-disposition');
      let filename = buildInvoiceFilename(invoice) || `invoice_${invoice.id}.pdf`;
      if (contentDisposition) {
        const parts = contentDisposition.split(';').map(part => part.trim());
        const filenamePart = parts.find(part => part.toLowerCase().startsWith('filename='));
        if (filenamePart) {
          let extracted = filenamePart.substring('filename='.length);
          extracted = extracted.replace(/^"|"$/g, '');
          try {
            const decoded = decodeURIComponent(extracted);
            if (decoded) {
              filename = decoded;
            }
          } catch (error) {
            if (extracted) {
              filename = extracted;
            }
          }
        }
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Invoice PDF downloaded');
    } catch (error) {
      console.error('Download PDF error:', error);
      toast.error('Failed to download PDF');
    }
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.name || 'Unknown';
  const getProjectName = (id) => projects.find(p => p.id === id)?.name || 'Unknown';
  const getPONumber = (id) => pos.find(p => p.id === id)?.po_number || '-';

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Issued': 'bg-blue-100 text-blue-800',
      'Viewed': 'bg-blue-100 text-blue-800',
      'Approved': 'bg-blue-100 text-blue-800',
      'Partial': 'bg-yellow-100 text-yellow-800',
      'Overdue': 'bg-red-100 text-red-800',
      'Paid': 'bg-utis-sage/20 text-utis-forest',
      'Void': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleFilterReset = () => {
    setStatusFilter('open');
    setClientFilter('all');
    setSearchTerm('');
  };
  const metrics = useMemo(() => {
    return invoices.reduce(
      (acc, invoice) => {
        const outstanding = Number(invoice.balance ?? Math.max(invoice.total - (invoice.amount_paid || 0), 0));
        if (OPEN_STATUSES.includes(invoice.domainStatus)) {
          acc.openBalance += outstanding;
          acc.openCount += 1;
        }
        if (invoice.domainStatus === 'overdue') {
          acc.overdueBalance += outstanding;
          acc.overdueCount += 1;
        }
        if (invoice.domainStatus === 'paid') {
          acc.paidAmount += invoice.amount_paid || invoice.total;
          acc.paidCount += 1;
        }
        if (invoice.domainStatus === 'draft') {
          acc.draftCount += 1;
        }
        acc.totalCount += 1;
        return acc;
      },
      {
        openBalance: 0,
        overdueBalance: 0,
        paidAmount: 0,
        draftCount: 0,
        openCount: 0,
        overdueCount: 0,
        paidCount: 0,
        totalCount: 0
      }
    );
  }, [invoices]);

  const matchesStatusFilter = (invoice) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'open') {
      return OPEN_STATUSES.includes(invoice.domainStatus);
    }
    return invoice.domainStatus === statusFilter;
  };

  const filteredInvoices = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const list = invoices.filter((invoice) => {
      if (!matchesStatusFilter(invoice)) {
        return false;
      }
      if (clientFilter !== 'all' && invoice.client_id !== clientFilter) {
        return false;
      }
      if (!normalizedSearch) {
        return true;
      }
      const haystacks = [
        invoice.invoice_no,
        getClientName(invoice.client_id),
        getProjectName(invoice.project_id),
        getPONumber(invoice.po_id),
        invoice.notes
      ];
      return haystacks.some((value) =>
        value && value.toString().toLowerCase().includes(normalizedSearch)
      );
    });

    return list.sort((a, b) => {
      const dueA = a.due_date ? new Date(a.due_date).getTime() : Number.POSITIVE_INFINITY;
      const dueB = b.due_date ? new Date(b.due_date).getTime() : Number.POSITIVE_INFINITY;
      return dueA - dueB;
    });
  }, [invoices, statusFilter, clientFilter, searchTerm]);

  const filteredOutstandingTotal = useMemo(
    () => filteredInvoices.reduce((sum, invoice) => sum + (invoice.balance ?? 0), 0),
    [filteredInvoices]
  );

  const upcomingInvoices = useMemo(() => {
    return filteredInvoices
      .filter((invoice) => OPEN_STATUSES.includes(invoice.domainStatus) && invoice.due_date)
      .slice()
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
      .slice(0, 5);
  }, [filteredInvoices]);

  const statusDistribution = useMemo(() => {
    const aggregate = invoices.reduce((acc, invoice) => {
      const key = invoice.domainStatus;
      if (!acc[key]) {
        acc[key] = { count: 0, balance: 0 };
      }
      acc[key].count += 1;
      acc[key].balance += invoice.balance ?? Math.max(invoice.total - (invoice.amount_paid || 0), 0);
      return acc;
    }, {});

    return Object.entries(aggregate).sort((a, b) => {
      const idxA = STATUS_ORDER.indexOf(a[0]);
      const idxB = STATUS_ORDER.indexOf(b[0]);
      if (idxA === -1 && idxB === -1) {
        return a[0].localeCompare(b[0]);
      }
      if (idxA === -1) return 1;
      if (idxB === -1) return -1;
      return idxA - idxB;
    });
  }, [invoices]);

  const filtersActive =
    statusFilter !== 'open' || clientFilter !== 'all' || searchTerm.trim() !== '';

  if (loading) {
    return <LogoLoader message="Loading invoices..." />;
  }

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-600 mt-1">Issue, track, and collect client invoices</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={loadData}
            className="flex items-center gap-2"
          >
            <RefreshCcw className="w-4 h-4" />
            Refresh
          </Button>
          <Button onClick={() => setShowDialog(true)} className="bg-utis-forest hover:bg-utis-forest/90 text-white" data-testid="generate-invoice-button">
            <Plus className="w-4 h-4 mr-2" />
            Generate Invoice
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card className="border-utis-sage">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Open balance</p>
                <p className="text-2xl font-semibold text-gray-900">{formatCurrency(metrics.openBalance)}</p>
                <p className="text-xs text-gray-500 mt-1">{metrics.openCount} open invoice{metrics.openCount === 1 ? '' : 's'}</p>
              </div>
              <div className="rounded-full bg-utis-sage/30 p-2">
                <TrendingUp className="w-6 h-6 text-utis-forest" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-100">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overdue amount</p>
                <p className="text-2xl font-semibold text-red-600">{formatCurrency(metrics.overdueBalance)}</p>
                <p className="text-xs text-gray-500 mt-1">{metrics.overdueCount} invoice{metrics.overdueCount === 1 ? '' : 's'} overdue</p>
              </div>
              <div className="rounded-full bg-red-100 p-2">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-100">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Collected</p>
                <p className="text-2xl font-semibold text-utis-forest">{formatCurrency(metrics.paidAmount)}</p>
                <p className="text-xs text-gray-500 mt-1">{metrics.paidCount} invoice{metrics.paidCount === 1 ? '' : 's'} paid</p>
              </div>
              <div className="rounded-full bg-utis-sage/30 p-2">
                <CheckCircle className="w-6 h-6 text-utis-forest" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-gray-200">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Drafts awaiting issue</p>
                <p className="text-2xl font-semibold text-gray-900">{metrics.draftCount}</p>
                <p className="text-xs text-gray-500 mt-1">Total invoices: {metrics.totalCount}</p>
              </div>
              <div className="rounded-full bg-gray-100 p-2">
                <FileText className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label className="text-sm text-gray-700">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_FILTERS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-gray-700">Client</Label>
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All clients</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-gray-700">Search</Label>
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Invoice, client, project or PO"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-gray-600">
              Showing {filteredInvoices.length} of {invoices.length} invoices • Outstanding {formatCurrency(filteredOutstandingTotal)}
            </div>
            <div className="flex flex-wrap gap-2">
              {filtersActive && (
                <Button variant="ghost" size="sm" onClick={handleFilterReset}>
                  Clear filters
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant={viewMode === 'table' ? 'default' : 'outline'}
                onClick={() => setViewMode('table')}
                className="flex items-center gap-2"
              >
                <List className="w-4 h-4" />
                Table
              </Button>
              <Button
                type="button"
                size="sm"
                variant={viewMode === 'cards' ? 'default' : 'outline'}
                onClick={() => setViewMode('cards')}
                className="flex items-center gap-2"
              >
                <LayoutGrid className="w-4 h-4" />
                Cards
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
        <Card>
          <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-xl">Invoice register</CardTitle>
              <CardDescription>
                {filteredInvoices.length === 0
                  ? 'No invoices match the current filters.'
                  : 'Manage billing activity and download issued documents.'}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {filteredInvoices.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No invoices to display. Adjust your filters or generate a new invoice.
              </div>
            ) : viewMode === 'table' ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>PO</TableHead>
                    <TableHead>Issued</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Paid</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">
                        <button
                          onClick={() => viewInvoiceDetails(invoice)}
                          className="text-utis-forest hover:underline"
                        >
                          {invoice.invoice_no}
                        </button>
                      </TableCell>
                      <TableCell>{getClientName(invoice.client_id)}</TableCell>
                      <TableCell>{getProjectName(invoice.project_id)}</TableCell>
                      <TableCell>{getPONumber(invoice.po_id)}</TableCell>
                      <TableCell>{formatDisplayDate(invoice.invoice_date, '-')}</TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>{formatDisplayDate(invoice.due_date, '-')}</span>
                          <span className="text-xs text-gray-500">{describeDueDate(invoice.due_date)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{formatCurrency(invoice.total)}</TableCell>
                      <TableCell>{formatCurrency(invoice.amount_paid || 0)}</TableCell>
                      <TableCell className={invoice.balance > 0 ? 'text-orange-600 font-medium' : 'text-gray-600'}>
                        {formatCurrency(invoice.balance)}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(invoice.status)}>{invoice.status}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-wrap gap-2">
                            {invoice.domainStatus === 'draft' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleStatusUpdate(invoice.id, 'sent')}
                                className="text-xs"
                              >
                                Issue
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => viewInvoiceDetails(invoice)}
                              className="text-xs"
                            >
                              Details
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadInvoicePDF(invoice)}
                              className="text-xs"
                              title="Download PDF"
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                          </div>
                          <Label className="flex items-center gap-2 text-xs text-gray-600">
                            <Checkbox
                              id={`po-summary-${invoice.id}`}
                              checked={!!poSummarySelections[invoice.id]}
                              onCheckedChange={(checked) => setIncludePoSummary(invoice.id, checked === true)}
                              aria-label="Include PO details"
                            />
                            Include PO details
                          </Label>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {filteredInvoices.map((invoice) => (
                  <Card key={invoice.id} className="border border-gray-200">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <CardTitle className="text-lg">{invoice.invoice_no}</CardTitle>
                          <CardDescription>{getClientName(invoice.client_id)}</CardDescription>
                        </div>
                        <Badge className={getStatusColor(invoice.status)}>{invoice.status}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-gray-500">Issued</p>
                          <p className="font-medium">{formatDisplayDate(invoice.invoice_date, '-')}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Due</p>
                          <p className="font-medium">{formatDisplayDate(invoice.due_date, '-')}</p>
                          <p className="text-xs text-gray-500">{describeDueDate(invoice.due_date)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Total</p>
                          <p className="font-semibold">{formatCurrency(invoice.total)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Balance</p>
                          <p className={`font-semibold ${invoice.balance > 0 ? 'text-orange-600' : 'text-gray-600'}`}>
                            {formatCurrency(invoice.balance)}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-3">
                        <Label className="flex items-center gap-2 text-xs text-gray-600">
                          <Checkbox
                            id={`po-summary-card-${invoice.id}`}
                            checked={!!poSummarySelections[invoice.id]}
                            onCheckedChange={(checked) => setIncludePoSummary(invoice.id, checked === true)}
                            aria-label="Include PO details"
                          />
                          Include PO details in PDF
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {invoice.domainStatus === 'draft' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusUpdate(invoice.id, 'sent')}
                            >
                              Issue
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => viewInvoiceDetails(invoice)}>
                            Details
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadInvoicePDF(invoice)}
                            title="Download PDF"
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-utis-forest" />
                Upcoming due
              </CardTitle>
              <CardDescription>Invoices approaching their due date</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {upcomingInvoices.length === 0 ? (
                <p className="text-sm text-gray-500">All caught up—no upcoming invoices in the selected view.</p>
              ) : (
                upcomingInvoices.map((invoice) => {
                  const diff = calculateDaysUntil(invoice.due_date);
                  const dueTone = diff !== null && diff < 0 ? 'text-red-600' : 'text-gray-500';
                  return (
                    <div key={invoice.id} className="flex items-center justify-between rounded-lg border border-gray-100 p-3">
                      <div>
                        <p className="font-medium text-sm">{invoice.invoice_no}</p>
                        <p className="text-xs text-gray-500">{getClientName(invoice.client_id)}</p>
                      </div>
                      <div className="text-right text-sm">
                        <p className="font-semibold">{formatCurrency(invoice.balance)}</p>
                        <p className={`text-xs ${dueTone}`}>{describeDueDate(invoice.due_date)}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Status mix</CardTitle>
              <CardDescription>Breakdown of invoice counts and balances</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {statusDistribution.length === 0 ? (
                <p className="text-sm text-gray-500">No invoices available.</p>
              ) : (
                statusDistribution.map(([status, info]) => (
                  <div key={status} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusColor(STATUS_LABELS[status] || status)}>
                        {STATUS_LABELS[status] || status}
                      </Badge>
                      <span className="text-gray-600">{info.count} invoice{info.count === 1 ? '' : 's'}</span>
                    </div>
                    <span className="font-medium">{formatCurrency(info.balance)}</span>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Generate Invoice Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Invoice</DialogTitle>
            <DialogDescription>Select period and parameters to generate invoice from approved time entries</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleGenerate} className="space-y-4">
            <div>
              <Label>Client *</Label>
              <Select
                value={formData.client_id}
                onValueChange={(val) => setFormData({...formData, client_id: val, project_id: '', po_id: ''})}
                required
                data-testid="client-select"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.filter(c => c.is_active).map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Project {!formData.po_id && '*'}</Label>
              <Select
                value={formData.project_id}
                onValueChange={(val) => setFormData({...formData, project_id: val, po_id: val ? '' : formData.po_id})}
                data-testid="project-select"
                disabled={!!formData.po_id || !formData.client_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !formData.client_id ? "Select client first" :
                    formData.po_id ? "Clear PO to select Project" :
                    "Select project"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {projects.filter(p => p.client_id === formData.client_id && p.is_active).map(project => (
                    <SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.project_id && (
                <button
                  type="button"
                  onClick={() => setFormData({...formData, project_id: ''})}
                  className="text-xs text-red-600 hover:underline mt-1"
                >
                  Clear selection
                </button>
              )}
            </div>

            <div className="flex items-center justify-center text-sm text-gray-500">
              - OR -
            </div>

            <div>
              <Label>Purchase Order {!formData.project_id && '*'}</Label>
              <Select
                value={formData.po_id}
                onValueChange={(val) => setFormData({...formData, po_id: val, project_id: val ? '' : formData.project_id})}
                data-testid="po-select"
                disabled={!!formData.project_id || !formData.client_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !formData.client_id ? "Select client first" :
                    formData.project_id ? "Clear Project to select PO" :
                    "Select PO"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {pos.filter(po => po.client_id === formData.client_id && po.is_active).map(po => (
                    <SelectItem key={po.id} value={po.id}>{po.po_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.po_id && (
                <button
                  type="button"
                  onClick={() => setFormData({...formData, po_id: ''})}
                  className="text-xs text-red-600 hover:underline mt-1"
                >
                  Clear selection
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period Start *</Label>
                <Input type="date" name="period_start" value={formData.period_start} onChange={(e) => setFormData({...formData, period_start: e.target.value})} required />
              </div>
              <div>
                <Label>Period End *</Label>
                <Input type="date" name="period_end" value={formData.period_end} onChange={(e) => setFormData({...formData, period_end: e.target.value})} required />
              </div>
            </div>
            <div>
              <Label>Tax Rate (%)</Label>
              <Input type="number" name="tax_rate" step="0.01" value={formData.tax_rate} onChange={(e) => setFormData({...formData, tax_rate: e.target.value})} placeholder="0" />
            </div>
            <Button type="submit" className="w-full bg-utis-forest hover:bg-utis-forest/90 text-white">Generate Invoice</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invoice Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Invoice Number</p>
                  <p className="font-bold text-lg">{selectedInvoice.invoice_no}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <Badge className={getStatusColor(selectedInvoice.status)}>{selectedInvoice.status}</Badge>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Client</p>
                  <p className="font-medium">{getClientName(selectedInvoice.client_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Project</p>
                  <p className="font-medium">{getProjectName(selectedInvoice.project_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Invoice Date</p>
                  <p className="font-medium">{formatDisplayDate(selectedInvoice.invoice_date, '-')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Due Date</p>
                  <p className="font-medium">{formatDisplayDate(selectedInvoice.due_date, '-')}</p>
                  <p className="text-xs text-gray-500">{describeDueDate(selectedInvoice.due_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">PO Reference</p>
                  <p className="font-medium">{getPONumber(selectedInvoice.po_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Balance</p>
                  <p className={`font-semibold ${selectedInvoice.balance > 0 ? 'text-orange-600' : 'text-gray-600'}`}>
                    {formatCurrency(selectedInvoice.balance)}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="font-bold mb-2">Line Items</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedInvoice.lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>{line.description}</TableCell>
                        <TableCell>{line.quantity_hours || '-'}</TableCell>
                        <TableCell>{line.rate ? formatCurrency(line.rate) : '-'}</TableCell>
                        <TableCell className="font-medium">{formatCurrency(line.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">{formatCurrency(selectedInvoice.subtotal)}</span>
                </div>
                {selectedInvoice.tax_amount > 0 && (
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Tax ({(selectedInvoice.tax_rate * 100).toFixed(1)}%):</span>
                    <span className="font-medium">{formatCurrency(selectedInvoice.tax_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-utis-forest">{formatCurrency(selectedInvoice.total)}</span>
                </div>
                <div className="flex justify-between mt-2 text-sm">
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-medium">{formatCurrency(selectedInvoice.amount_paid || 0)}</span>
                </div>
                <div className="flex justify-between mt-1 text-sm">
                  <span className="text-gray-600">Outstanding:</span>
                  <span className="font-medium text-orange-600">{formatCurrency(selectedInvoice.balance)}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
